import { createNoiseGenerator, ObjectType } from '@dreamgame/shared';

// ============================================================================
// Forest rendering utilities - shared by ObjectLayer, ForestEffectsLayer,
// ForestCanopyLayer for consistent tree positioning and visual variation.
// ============================================================================

// Visual scale multiplier for tree sprites (purely cosmetic, footprint unchanged)
export const TREE_RENDER_SCALE = 3;

// Maximum jitter in pixels (deterministic per-tree)
export const TREE_JITTER_X = 4;
export const TREE_JITTER_Y = 3;

// Deterministic spatial hash for tree jitter (large primes, zero allocation)
export const treeJitterX = ({ tileX, tileY }: { tileX: number; tileY: number }): number => {
  const h = ((tileX * 73856093) ^ (tileY * 19349663)) >>> 0;
  return (h % (TREE_JITTER_X * 2 + 1)) - TREE_JITTER_X;
};

export const treeJitterY = ({ tileX, tileY }: { tileX: number; tileY: number }): number => {
  const h = ((tileX * 83492791) ^ (tileY * 48611953)) >>> 0;
  return (h % (TREE_JITTER_Y * 2 + 1)) - TREE_JITTER_Y;
};

// Per-tree tint color variants (6 variants: neutral, cool, warm, dark, bright, olive)
export const TREE_TINTS: ReadonlyArray<number> = [
  0xffffff, // neutral (no tint)
  0xe8f0e8, // cool green
  0xf0ece0, // warm
  0xe0e8d8, // dark green
  0xf0f8e8, // bright
  0xe8e8d0, // olive
];

const _treeBiomeNoise = createNoiseGenerator({ seed: 9323 });
const _treeVariationNoise = createNoiseGenerator({ seed: 9341 });

// Deterministic tint index for a tree at given position
export const treeTintIndex = ({ tileX, tileY }: { tileX: number; tileY: number }): number => {
  // Tie tint to biome-scale fields so dense forest trends cooler/darker while
  // clearing edges trend warmer/lighter, with deterministic local variation.
  const biome = (_treeBiomeNoise.noise2D({ x: tileX * 0.06, y: tileY * 0.06 }) + 1) / 2;
  const variation = (_treeVariationNoise.noise2D({ x: tileX * 0.24, y: tileY * 0.24 }) + 1) / 2;

  if (biome > 0.78) {
    return variation > 0.4 ? 3 : 1;
  }
  if (biome > 0.62) {
    return variation > 0.72 ? 0 : 1;
  }
  if (biome > 0.44) {
    return variation > 0.78 ? 5 : 0;
  }
  if (biome > 0.26) {
    return variation > 0.55 ? 5 : 2;
  }
  return variation > 0.42 ? 4 : 2;
};

// Tree types that receive oversized rendering + canopy effects
// Uses `number` to match WorldObjectSnapshot.objectType (protocol type)
export const TREE_OBJECT_TYPES: ReadonlySet<number> = new Set([
  ObjectType.TREE_OAK,
  ObjectType.TREE_PINE,
  ObjectType.TREE_BIRCH,
]);

// Per-tree-type canopy colors for the canopy overlay layer
export const CANOPY_COLORS: Readonly<Record<number, number>> = {
  [ObjectType.TREE_OAK]: 0x3a7a2a, // warm green
  [ObjectType.TREE_PINE]: 0x2a5a2a, // dark green
  [ObjectType.TREE_BIRCH]: 0x5a9a4a, // light green
};
